import React, { useContext, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import IconButton from '@mui/material/IconButton';
import { STALE_TIME } from 'business/constants';
import {
    BudgetProposalT,
    CellRendererParams,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { useAddProject } from 'routes/Admin/Budget/ProjectManagement/hooks/useAddProject';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';
import { getInscriptionInformation } from '../../dataHelpers';
import { useAddInscriptionProjectMutation } from '../../hooks/inscriptionsProjects';
import ProjectsChipsDisplay from './ProjectsChipsDisplay';
import ProjectsSelectionMenu from './ProjectsSelectionMenu';
import { useAddProjectToInscription } from './hooks/useAddProjectToInscription';
import { useRemoveProjectFromInscription } from './hooks/useRemoveProjectFromInscription';
import {
    filterAssignedProjects,
    filterUnassignedProjects,
} from './ProjectsHelpers';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

const StyBox = styled(Box, {
    name: 'StyBox',
})({
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    overflowY: 'hidden',
});

type ProjectsCellRendererParams =
    CellRendererParams<InscriptionWithMetadata> & {
        value: number[] | string;
        budgetProposals: BudgetProposalT[];
        inscriptionIdColumn?: keyof InscriptionT;
        queryCacheKey: InscriptionQueryKey;
    };

export default function ProjectsCellRenderer({
    value,
    data,
    budgetProposals,
    inscriptionIdColumn,
    queryCacheKey,
}: ProjectsCellRendererParams): React.ReactElement {
    const [anchorEl, setAnchorEl] = useState<
        (EventTarget & HTMLButtonElement) | null
    >(null);
    const { isPending: isLoadingProjects } = useAddProject();
    const { isPending: isLoadingInscriptionsProjects } =
        useAddInscriptionProjectMutation();

    const assignedProjectsId = Array.isArray(value) // value is a string only when grid is grouped by project.
        ? value
        : [parseInt(value, 10)];

    const statusesSettings = useContext(StatusSettingsContext);

    const { projects } = useGetProjects({ staleTime: STALE_TIME }); // no need to re-fetch it each time we expand a row

    const { inscriptionId, canEdit } = getInscriptionInformation(
        data,
        budgetProposals,
        statusesSettings,
        inscriptionIdColumn,
    );

    const { addProjectToInscription } = useAddProjectToInscription(
        inscriptionId,
        projects,
        queryCacheKey,
        inscriptionIdColumn,
    );

    const { removeProjectFromInscription } = useRemoveProjectFromInscription(
        inscriptionId,
        queryCacheKey,
        inscriptionIdColumn,
    );

    const closeProjectForm = () => {
        setAnchorEl(null);
    };

    const assignedProjects = filterAssignedProjects(
        projects,
        assignedProjectsId,
    );
    const unassignedProjects = filterUnassignedProjects(
        projects,
        assignedProjects,
    );

    const canAddProject =
        !isLoadingInscriptionsProjects && !isLoadingProjects && canEdit;

    const addProjectButton = (
        <IconButton
            size="small"
            onClick={(event) => {
                setAnchorEl(event.currentTarget);
            }}
        >
            <AddCircleRoundedIcon />
        </IconButton>
    );

    return (
        <StyBox>
            {canAddProject && addProjectButton}
            <ProjectsChipsDisplay
                assignedProjects={assignedProjects}
                canEdit={canEdit}
                removeProjectFromInscription={removeProjectFromInscription}
            />
            <ProjectsSelectionMenu
                anchorEl={anchorEl}
                closeProjectForm={closeProjectForm}
                unassignedProjects={unassignedProjects}
                addProjectToInscription={addProjectToInscription}
            />
        </StyBox>
    );
}
